import jsPDF from "jspdf";

// Convierte un número a letras en español (solo parte entera, 0 - 999999)
function numberToWordsEs(num) {
  const unidades = [
    "CERO",
    "UNO",
    "DOS",
    "TRES",
    "CUATRO",
    "CINCO",
    "SEIS",
    "SIETE",
    "OCHO",
    "NUEVE",
    "DIEZ",
    "ONCE",
    "DOCE",
    "TRECE",
    "CATORCE",
    "QUINCE",
  ];

  const decenas = [
    "",
    "DIEZ",
    "VEINTE",
    "TREINTA",
    "CUARENTA",
    "CINCUENTA",
    "SESENTA",
    "SETENTA",
    "OCHENTA",
    "NOVENTA",
  ];

  const centenas = [
    "",
    "CIENTO",
    "DOSCIENTOS",
    "TRESCIENTOS",
    "CUATROCIENTOS",
    "QUINIENTOS",
    "SEISCIENTOS",
    "SETECIENTOS",
    "OCHOCIENTOS",
    "NOVECIENTOS",
  ];

  if (num === 0) return "CERO";
  if (num === 100) return "CIEN";

  const toWordsBelowThousand = (n) => {
    let words = "";
    const c = Math.floor(n / 100);
    const d = Math.floor((n % 100) / 10);
    const u = n % 10;

    if (c) {
      words += centenas[c] + " ";
    }

    if (n % 100 <= 15 && n % 100 > 0) {
      words += unidades[n % 100];
    } else if (n % 100 > 15) {
      if (n % 100 >= 16 && n % 100 <= 19) {
        // DIECISEIS - DIECINUEVE
        words += "DIECI" + unidades[u].toLowerCase();
      } else if (n % 100 >= 20 && n % 100 <= 29) {
        if (u === 0) {
          words += "VEINTE";
        } else {
          words += "VEINTI" + unidades[u].toLowerCase();
        }
      } else {
        words += decenas[d];
        if (u) {
          words += " Y " + unidades[u];
        }
      }
    }

    return words.trim();
  };

  let result = "";
  const miles = Math.floor(num / 1000);
  const resto = num % 1000;

  if (miles) {
    if (miles === 1) {
      result += "MIL";
    } else {
      result += toWordsBelowThousand(miles) + " MIL";
    }
  }

  if (resto) {
    if (result) result += " ";
    result += toWordsBelowThousand(resto);
  }

  return result.trim();
}

// Carga el logo desde public y lo convierte a dataURL
async function loadLogoDataUrl() {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = function () {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve(null);
      img.src = "/lux_logo.PNG"; // ubicado en public/lux_logo.PNG
    } catch (e) {
      resolve(null);
    }
  });
}

// Agrega el parámetro isEmail para distinguir el contexto de envío por email
export async function generateReciboPDF({ student, charges, paymentData = {}, descargar = false, isEmail = false, lugar = null, fechaElaboracion = null }) {
  const doc = new jsPDF();

  // Obtener nombre del cajero si solo se tiene el id
  let cajeroNombre = paymentData.cajero;
  if ((!cajeroNombre || typeof cajeroNombre !== 'string' || cajeroNombre.trim() === '') && paymentData.usuario_creacion) {
    // Intentar obtener de localStorage (donde se guarda el usuario actual)
    try {
      const usuariosCache = JSON.parse(localStorage.getItem('usuariosCache') || '{}');
      if (usuariosCache && usuariosCache[paymentData.usuario_creacion]) {
        cajeroNombre = usuariosCache[paymentData.usuario_creacion];
      } else {
        // Si no está en cache, intentar obtener del usuario actual
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
        if (currentUser && (currentUser.id_usuario === paymentData.usuario_creacion || currentUser.id === paymentData.usuario_creacion)) {
          cajeroNombre = currentUser.nombre_completo || currentUser.nombre || currentUser.usuario || currentUser.email || '';
        } else {
          // Si no está, intentar fetch al backend (requiere endpoint disponible)
          try {
            const token = localStorage.getItem('authToken');
            const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
            const res = await fetch(`${apiBase}/sistema/usuarios/${paymentData.usuario_creacion}/`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const userData = await res.json();
              cajeroNombre = userData.nombre_completo || userData.nombre || userData.usuario || userData.email || '';
              // Guardar en cache para futuras consultas
              usuariosCache[paymentData.usuario_creacion] = cajeroNombre;
              localStorage.setItem('usuariosCache', JSON.stringify(usuariosCache));
            }
          } catch {}
        }
      }
    } catch {}
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;

  // Obtener el recargo total mostrado en la UI (si existe)
  let recargoTotal = 0;
  if (typeof window !== 'undefined' && typeof window.__lastDisplayRecargos === 'number') {
    recargoTotal = window.__lastDisplayRecargos;
  } else if (paymentData.recargoTotal !== undefined) {
    recargoTotal = paymentData.recargoTotal;
  }

  // Calcular el total mostrado en la tabla (suma de monto_pagado + recargo)
  const visibleCharges = (charges || []).filter((charge) => charge && (charge.monto || charge.id_concepto));
  const totalImporte = visibleCharges.reduce((sum, charge) => {
    let pagoCargo = parseFloat(charge.monto) || 0;
    if (charge.monto_pagado !== undefined && !isNaN(parseFloat(charge.monto_pagado))) {
      const pagado = parseFloat(charge.monto_pagado);
      if (pagado > 0) pagoCargo = pagado;
    }
    let pagoRecargo = 0;
    if (charge.monto_recargo !== undefined) {
      pagoRecargo = parseFloat(charge.monto_recargo) || 0;
    }
    return sum + pagoCargo + pagoRecargo;
  }, 0);
  const pesos = Math.floor(totalImporte);
  const centavos = Math.round((totalImporte - pesos) * 100);
  const totalEnLetras = `${numberToWordsEs(pesos)} PESOS ${String(centavos).padStart(2, "0")}/100 M.N.`;


  // Construir línea de "Cliente" con nombre y dirección (soporta ambos formatos)
  const buildClientLine = (st) => {
    if (!st) return "";

    const dir = st.direccion || {};
    // Prioridad: campo directo, si no, buscar en subobjeto direccion
    const get = (field) => st[field] || dir[field] || "";

    const name = st.nombre || st.nombre_completo || "";
    const parts = [];
    if (name) parts.push(name);

    // Calle y números
    if (get("calle")) {
      let calle = get("calle").trim();
      const numeros = [];
      if (get("numero_ext")) numeros.push(String(get("numero_ext")).trim());
      if (get("numero_int")) numeros.push(`Int ${String(get("numero_int")).trim()}`);
      if (numeros.length > 0) {
        calle += ` ${numeros.join(" ")}`;
      }
      parts.push(calle);
    }

    if (get("colonia")) {
      parts.push(`Col ${get("colonia")}`);
    }

    if (get("codigo_postal")) {
      parts.push(String(get("codigo_postal")));
    }

    const ciudadEstado = [get("ciudad"), get("estado")].filter(Boolean).join(" ");
    if (ciudadEstado) {
      parts.push(ciudadEstado);
    }

    if (get("pais")) {
      parts.push(get("pais"));
    }

    return parts.join(", ");
  };

  const clientLine = buildClientLine(student);

  // Encabezado con logo a la izquierda y texto alineado a la izquierda
  const logoDataUrl = await loadLogoDataUrl();
  const logoSize = 18; // tamaño aproximado del logo
  const headerTopY = 12;
  const textStartX = margin + logoSize + 8; // texto a la derecha del logo

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "PNG", margin, headerTopY - 6, logoSize, logoSize);
  }

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Centro De Estudios Superiores CMP, A.C.", textStartX, 15);
  doc.setFont("helvetica", "normal");
  doc.text("Ave. Venustiano Carranza Nte. 614", textStartX, 20);
  doc.text("CENTRO", textStartX, 25);
  doc.text("MONTERREY, MONTERREY, N.L. C.P.64000", textStartX, 30);
  doc.text("R.F.C CES9912132F4", textStartX, 35);

  // Sección Cliente / Matrícula / Recibo (cajas)
  const topBoxY = 42;
  const clienteBoxWidth = 90;
  const matriculaBoxWidth = 35;
  const reciboBoxWidth = 55;
  const boxHeight = 24;

  // Etiquetas en negrita
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Cliente", margin, topBoxY - 2);
  doc.text("Matrícula", margin + clienteBoxWidth + 2, topBoxY - 2);

  // Cajas
  doc.rect(margin, topBoxY, clienteBoxWidth, boxHeight);
  doc.rect(margin + clienteBoxWidth, topBoxY, matriculaBoxWidth, boxHeight);
  doc.rect(pageWidth - margin - reciboBoxWidth, topBoxY, reciboBoxWidth, boxHeight);

  // Contenido cajas
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  // El nombre del cliente puede incluir dirección si está disponible
  // Separar nombre y dirección en líneas distintas

  let clienteNombre = student?.nombre || student?.nombre_completo || "";
  let clienteDireccion = "";
  if (clientLine && clienteNombre && clientLine.startsWith(clienteNombre + ", ")) {
    clienteDireccion = clientLine.substring((clienteNombre + ", ").length);
  } else if (clientLine && clientLine !== clienteNombre) {
    clienteDireccion = clientLine;
  }
  let clienteLines = [];
  if (clienteNombre) clienteLines.push(clienteNombre);
  // Si hay dirección, agregarla (aunque no haya nombre)
  if (clienteDireccion) {
    const direccionLines = doc.splitTextToSize(clienteDireccion, clienteBoxWidth - 4);
    clienteLines.push(...direccionLines);
  } else if (!clienteNombre && clientLine) {
    // Si no hay nombre pero sí dirección completa
    const direccionLines = doc.splitTextToSize(clientLine, clienteBoxWidth - 4);
    clienteLines.push(...direccionLines);
  }
  // Si no hay nombre ni dirección, mostrar vacío
  if (clienteLines.length === 0) clienteLines = [""];
  doc.text(clienteLines, margin + 2, topBoxY + 6);
  doc.text(student?.matricula || "", margin + clienteBoxWidth + 2, topBoxY + 6);

  // Caja RECIBO con estructura:
  // 1) Barra negra "RECIBO"
  // 2) Línea blanca para código de recibo
  // 3) Barra negra "LUGAR Y FECHA DE ELABORACIÓN"
  // 4) Texto de ciudad y fecha
  const reciboX = pageWidth - margin - reciboBoxWidth;
  const barraAltura = 6;
  let codigoRecibo = paymentData.codigo_recibo || paymentData.folio || paymentData.recibo || "";
  // Si no viene un código desde el backend, generamos uno de ejemplo estilo "RRM 000000"
  if (!codigoRecibo) {
    const random = Math.floor(Math.random() * 1000000);
    codigoRecibo = `RRM ${String(random).padStart(6, "0")}`;
  }

  // 1) Barra superior: RECIBO
  doc.setFillColor(0, 0, 0);
  doc.rect(reciboX, topBoxY, reciboBoxWidth, barraAltura, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("RECIBO", reciboX + reciboBoxWidth / 2, topBoxY + 4.5, { align: "center" });

  // 2) Línea blanca para el código del recibo
  const codigoY = topBoxY + barraAltura + 4;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  doc.text(String(codigoRecibo), reciboX + reciboBoxWidth / 2, codigoY, { align: "center" });

  // 3) Segunda barra negra: LUGAR Y FECHA DE ELABORACIÓN
  const segundaBarraY = topBoxY + barraAltura * 2;
  doc.setFillColor(0, 0, 0);
  doc.rect(reciboX, segundaBarraY, reciboBoxWidth, barraAltura, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.text("LUGAR Y FECHA DE ELABORACIÓN", reciboX + reciboBoxWidth / 2, segundaBarraY + 4.3, { align: "center" });

  // 4) Texto de ciudad y fecha debajo de las barras negras
  const fechaTextoY = segundaBarraY + barraAltura + 6;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  // Mejorar el lugar: usar parámetro lugar si viene, si no usar ciudad, campus, o MONTERREY por defecto
  const lugarFinal = (lugar !== null && lugar !== undefined) ? String(lugar).toUpperCase() : (student?.campus ? String(student.campus).toUpperCase() : "MONTERREY");
  let fechaStr = '';
  if (fechaElaboracion) {
    // Robust date parsing: handle Date, ISO, and ambiguous formats
    let anio, mes, dia;
    if (fechaElaboracion instanceof Date && !isNaN(fechaElaboracion)) {
      // Direct Date object
      dia = String(fechaElaboracion.getDate()).padStart(2, '0');
      mes = String(fechaElaboracion.getMonth() + 1).padStart(2, '0');
      anio = fechaElaboracion.getFullYear();
    } else if (typeof fechaElaboracion === 'string') {
      // Try ISO (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
      let match = fechaElaboracion.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        anio = match[1];
        mes = match[2];
        dia = match[3];
      } else {
        // Try DD/MM/YYYY or MM/DD/YYYY
        match = fechaElaboracion.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
        if (match) {
          // Heuristic: if month > 12, it's DD/MM/YYYY, else MM/DD/YYYY (US)
          if (parseInt(match[2], 10) > 12) {
            // Invalid month, so must be DD/MM/YYYY
            dia = match[1];
            mes = match[2];
            anio = match[3];
          } else {
            // Could be either, but default to DD/MM/YYYY
            dia = match[1];
            mes = match[2];
            anio = match[3];
          }
        } else {
          // Fallback: parse as Date
          const fechaObj = new Date(fechaElaboracion);
          dia = String(fechaObj.getDate()).padStart(2, '0');
          mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
          anio = fechaObj.getFullYear();
        }
      }
    } else {
      // Fallback: parse as Date
      const fechaObj = new Date(fechaElaboracion);
      dia = String(fechaObj.getDate()).padStart(2, '0');
      mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
      anio = fechaObj.getFullYear();
    }
    fechaStr = `${dia}/${mes}/${anio}`;
    doc.text(
      `${lugarFinal}, ${fechaStr}`,
      reciboX + reciboBoxWidth / 2,
      fechaTextoY,
      { align: "center" }
    );
  } else {
    // Si no, usar fecha y hora actuales
    const fechaObj = new Date();
    const dia = String(fechaObj.getDate()).padStart(2, '0');
    const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
    const anio = fechaObj.getFullYear();
    fechaStr = `${dia}/${mes}/${anio}`;
    let hora = fechaObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    hora = hora.replace(/^0/, '');
    doc.text(
      `${lugarFinal}, ${fechaStr} ${hora}`,
      reciboX + reciboBoxWidth / 2,
      fechaTextoY,
      { align: "center" }
    );
  }

  // Datos del estudiante (Nombre / CURP / Sección / Programa / Beca)
  const datosY = topBoxY + boxHeight + 10;
  const rowHeight = 10;

  // Recuadro y encabezados dentro del recuadro
  doc.rect(margin, datosY, pageWidth - margin * 2, rowHeight);

  doc.setFontSize(8);
  const headerY = datosY + 3; // dentro del recuadro, cerca del borde superior
  doc.text("Nombre", margin + 2, headerY);
  doc.text("CURP", margin + 50, headerY);
  doc.text("Sección", margin + 100, headerY);
  doc.text("Programa", margin + 145, headerY);
  doc.text("Beca", margin + 175, headerY);

  // Datos del estudiante, un poco más abajo en el mismo recuadro
  const dataY = datosY + 7;
  doc.text(student?.nombre || "", margin + 2, dataY);
  doc.text((student?.curp || "").toString(), margin + 50, dataY);
  doc.text((student?.seccion || "").toString(), margin + 100, dataY);
  doc.text((student?.programa_nombre || "").toString(), margin + 145, dataY);
  doc.text((student?.beca_activa_nombre || "").toString(), margin + 175, dataY);

  // Tabla de cargos
  const tableStartY = datosY + rowHeight + 6;
  const tableTopY = tableStartY - 5;
  const tableWidth = pageWidth - margin * 2;

  // Encabezados de la tabla
  // Nota: CONCEPTO/DESCRIPCION se angostaron respecto al ancho original para
  // dejar espacio a la columna DESCUENTO BECA sin cambiar el ancho total de la tabla.
  // PAGO CARGO/PAGO RECARGO/IMPORTE conservan su ancho original.
  const colX = [
    margin, // CANT.
    margin + 15, // UNIDAD
    margin + 35, // CONCEPTO
    margin + 55, // DESCRIPCION
    margin + 95, // PAGO CARGO
    margin + 120, // DESCUENTO BECA
    margin + 145, // PAGO RECARGO
    margin + 170, // IMPORTE
    margin + tableWidth, // FIN TABLA
  ];

  // Fondo negro del encabezado y texto en blanco (más delgado)
  const headerHeight = 6;
  doc.setFillColor(0, 0, 0);
  doc.rect(margin, tableTopY, tableWidth, headerHeight, "F");

  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  const headerTextY = tableTopY + headerHeight - 1.2;
  doc.text("CANT.", (colX[0] + colX[1]) / 2, headerTextY, { align: "center" });
  doc.text("UNIDAD", (colX[1] + colX[2]) / 2, headerTextY, { align: "center" });
  doc.text("CONCEPTO", (colX[2] + colX[3]) / 2, headerTextY, { align: "center" });
  doc.text("DESCRIPCIÓN", (colX[3] + colX[4]) / 2, headerTextY, { align: "center" });
  doc.text("PAGO CARGO", (colX[4] + colX[5]) / 2, headerTextY, { align: "center" });
  doc.setFontSize(6); // "DESCUENTO BECA" es más largo; se reduce para que quepa en una línea
  doc.text("DESCUENTO BECA", (colX[5] + colX[6]) / 2, headerTextY, { align: "center" });
  doc.setFontSize(7);
  doc.text("PAGO RECARGO", (colX[6] + colX[7]) / 2, headerTextY, { align: "center" });
  doc.text("IMPORTE", (colX[7] + colX[8]) / 2, headerTextY, { align: "center" });

  // Filas de cargos
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0); // volver a texto negro para los datos
  let currentY = tableTopY + headerHeight;
  const baseLineHeight = 3.2;
  const cellPadding = 1;

  // visibleCharges ya está declarado arriba
  let lastRowLineY = tableTopY + headerHeight; // al menos hasta el encabezado

  // Obtener recargos actuales por cargo desde la UI (PendingChargesSection)
  let recargosPorCargo = {};
  if (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes)) {
    window.__lastAdeudosPendientes.forEach((adeudo) => {
      recargosPorCargo[adeudo.id] = adeudo.monto_recargo || 0;
    });
  }

  visibleCharges.forEach((charge) => {
    if (!charge) return;
    const cantidad = charge.cantidad || 1;
    const unidad = "SERVICIO";
    // Mostrar solo el código en CONCEPTO
    const concepto = charge.codigo_concepto || '';
    // Mostrar subcategoría y descripción en DESCRIPCIÓN
    const descripcion = (charge.subcategoria ? charge.subcategoria + ' - ' : '') + (charge.concepto || '');
    const monto = parseFloat(charge.monto) || 0;

    // Usar monto_recargo explícito si viene en el cargo, si no buscar en recargosPorCargo
    let pagoRecargo = 0;
    if (charge.monto_recargo !== undefined) {
      pagoRecargo = parseFloat(charge.monto_recargo) || 0;
    } else if (charge.id_cargo && recargosPorCargo[charge.id_cargo] !== undefined) {
      pagoRecargo = parseFloat(recargosPorCargo[charge.id_cargo]) || 0;
    }


    // Mostrar monto_pagado en PAGO CARGO si existe y es válido, si no, usar monto
    let pagoCargo = monto.toFixed(2);
    if (isEmail && charge.monto_pagado !== undefined) {
      const pagado = parseFloat(charge.monto_pagado);
      if (!isNaN(pagado) && pagado > 0) {
        pagoCargo = pagado.toFixed(2);
      }
    } else if (charge.monto_pagado !== undefined) {
      // fallback: si viene monto_pagado aunque no sea email, úsalo si es válido y mayor a 0
      const pagado = parseFloat(charge.monto_pagado);
      if (!isNaN(pagado) && pagado > 0) {
        pagoCargo = pagado.toFixed(2);
      }
    }

    const importe = (parseFloat(pagoCargo) + pagoRecargo).toFixed(2);
    const descuentoBeca = parseFloat(charge.descuento_beca) || 0;

    doc.setFontSize(7);
    const rowTopY = currentY;
    const conceptoLines = doc.splitTextToSize(String(concepto || ""), (colX[3] - colX[2]) - cellPadding * 2);
    const descripcionLines = doc.splitTextToSize(String(descripcion || ""), (colX[4] - colX[3]) - cellPadding * 2);
    const maxLines = Math.max(1, conceptoLines.length, descripcionLines.length);
    const rowHeightDynamic = Math.max(4, maxLines * baseLineHeight + 2);
    const rowTextY = rowTopY + 3;
    const rowCenterY = rowTopY + rowHeightDynamic / 2 + 1;

    doc.text(String(cantidad), (colX[0] + colX[1]) / 2, rowCenterY, { align: "center" });
    doc.text(unidad, (colX[1] + colX[2]) / 2, rowCenterY, { align: "center" });
    doc.text(conceptoLines.length ? conceptoLines : [""], colX[2] + cellPadding, rowTextY);
    doc.text(descripcionLines.length ? descripcionLines : [""], colX[3] + cellPadding, rowTextY);
    doc.text(`$${pagoCargo}`, (colX[4] + colX[5]) / 2, rowCenterY, { align: "center" });
    doc.text(`$${descuentoBeca.toFixed(2)}`, (colX[5] + colX[6]) / 2, rowCenterY, { align: "center" });
    doc.text(`$${pagoRecargo.toFixed(2)}`, (colX[6] + colX[7]) / 2, rowCenterY, { align: "center" });
    doc.text(`$${importe}`, (colX[7] + colX[8]) / 2, rowCenterY, { align: "center" });

    // Línea inferior de la fila
    const rowLineY = rowTopY + rowHeightDynamic;
    doc.line(margin, rowLineY, margin + tableWidth, rowLineY);
    lastRowLineY = rowLineY;

    currentY = rowLineY;
  });

  // Marco de la tabla (incluye encabezado y filas hasta la última línea dibujada)
  const tableBottomY = lastRowLineY;
  doc.setTextColor(0, 0, 0);
  doc.rect(margin, tableTopY, tableWidth, tableBottomY - tableTopY);

  // Líneas verticales de división de columnas
  colX.slice(1, -1).forEach((x) => {
    doc.line(x, tableTopY, x, tableBottomY);
  });

  // Cuadro de totales a la derecha, muy cercano al borde inferior de la tabla
  const totalsBoxY = tableBottomY + 1; // casi pegado a la tabla
  const totalsBoxWidth = 70;
  const totalsBoxX = pageWidth - margin - totalsBoxWidth;

  doc.rect(totalsBoxX, totalsBoxY, totalsBoxWidth, 20);
  doc.setFontSize(8);

  // Etiquetas en negrita
  doc.setFont("helvetica", "bold");
  doc.text("Subtotal exento", totalsBoxX + 4, totalsBoxY + 7);
  doc.text("TOTAL", totalsBoxX + 4, totalsBoxY + 15);

  // Valores en fuente normal
  doc.setFont("helvetica", "normal");
  doc.text(`$${totalImporte.toFixed(2)}`, totalsBoxX + totalsBoxWidth - 4, totalsBoxY + 7, { align: "right" });
  doc.text(`$${totalImporte.toFixed(2)}`, totalsBoxX + totalsBoxWidth - 4, totalsBoxY + 15, { align: "right" });

  // Cantidad con letra
  const cantidadLetraY = totalsBoxY + 26;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Cantidad Con Letra", margin, cantidadLetraY);
  doc.setFont("helvetica", "normal");
  doc.text(totalEnLetras, margin + 40, cantidadLetraY);

  // Forma de pago / Referencia / Cuenta de pago / Observaciones
  const infoY = cantidadLetraY + 10;

  doc.setFont("helvetica", "bold");
  doc.text("Forma de pago", margin, infoY);
  doc.setFont("helvetica", "normal");
  // Mostrar la forma de pago con formato legible
  const formatFormaPago = (str) => {
    if (!str) return "";
    return str
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/(^|\s)([a-záéíóúñ])/g, (m, p1, p2) => p1 + p2.toUpperCase());
  };
  doc.text(formatFormaPago(paymentData.forma_pago), margin + 40, infoY);

  doc.setFont("helvetica", "bold");
  doc.text("Referencia", margin, infoY + 8);
  doc.setFont("helvetica", "normal");
  doc.text(paymentData.referencia || paymentData.referencia_bancaria || "", margin + 40, infoY + 8);

  doc.setFont("helvetica", "bold");
  doc.text("Cuenta de pago", margin, infoY + 16);
  doc.setFont("helvetica", "normal");
  doc.text(paymentData.cuenta_pago || "", margin + 40, infoY + 16);

  doc.setFont("helvetica", "bold");
  doc.text("Observaciones", margin, infoY + 24);
  doc.setFont("helvetica", "normal");
  doc.text((paymentData.observaciones || paymentData.observacion || "").toLocaleUpperCase(), margin + 40, infoY + 24);

  // Cajero
  const cajeroY = infoY + 40;
  doc.setFont("helvetica", "bold");
  doc.text("Cajero", margin, cajeroY);
  doc.setFont("helvetica", "normal");
  // Alinear el nombre del cajero con el texto de observaciones
  doc.text((cajeroNombre || "").toLocaleUpperCase(), margin + 40, cajeroY, { align: "left" });

  // Obtener el PDF como data URL (base64)
  const dataUriString = doc.output("datauristring");
  if (descargar) {
    doc.save("recibo.pdf");
  }
  // Devolver el data URL para poder enviarlo por correo si se requiere
  return dataUriString;
}
